// src/pages/.../CreateNewsResponsablesModal.jsx
import React, { useState, useEffect } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../components/Button/Button";
import { useUser } from "../../contexts/UserContext";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const getInitialFormState = (authorId = "") => ({
  title: "",
  content: "",
  image_url: "",
  author_id: authorId,
  is_active: true,
  destinataires_news: "Responsables clubs",
});

const CreateNewsModal = ({ isOpen, onClose, onCreateSuccess }) => {
  const { user } = useUser();
  const [formData, setFormData] = useState(() =>
    getInitialFormState(user?.userId || "")
  );

  // Met à jour l'auteur si le user arrive après le premier render
  useEffect(() => {
    if (user?.userId) {
      setFormData((prev) => ({
        ...prev,
        author_id: user.userId,
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(getInitialFormState(user?.userId || ""));
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      content: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Utilisateur non authentifié.");
      return;
    }

    try {
      await onCreateSuccess(formData); // le parent gère l’API + les toasts
      setFormData(getInitialFormState(user.userId || ""));
      onClose();
    } catch (error) {
      console.error("Erreur lors de la création de l'actualité", error);
      toast.error("Erreur lors de la création de l'actualité.");
    }
  };

  const handleCancel = () => {
    setFormData(getInitialFormState(user?.userId || ""));
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="relative w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200/60 dark:border-slate-700/60">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-slate-200/70 dark:border-slate-700/70 bg-gradient-to-r from-primary to-primary/80 text-white rounded-t-2xl">
          <h3 className="text-base sm:text-lg font-bold uppercase tracking-wide font-montserrat">
            Créer une actualité
          </h3>
          <button
            type="button"
            onClick={handleCancel}
            className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-sm"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit}
          className="px-4 sm:px-6 py-4 space-y-6 text-slate-800 dark:text-slate-100"
        >
          {/* Section infos générales */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Informations générales
            </h4>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold">
                Titre de l’actualité
              </label>
              <input
                type="text"
                name="title"
                placeholder="Ex : Rappel des dates de rentrée, informations licences..."
                value={formData.title}
                onChange={handleChange}
                className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                required
              />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold">
                  Destinataires
                </label>
                <select
                  name="destinataires_news"
                  value={formData.destinataires_news}
                  onChange={handleChange}
                  className="select select-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                >
                  <option value="Tout public">Tout public</option>
                  <option value="Responsables clubs">
                    Responsables clubs
                  </option>
                </select>
              </div>

              <div className="flex items-center gap-2 mt-2 md:mt-6">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="form-checkbox h-4 w-4 text-primary rounded"
                />
                <label
                  htmlFor="is_active"
                  className="text-xs sm:text-sm text-slate-700 dark:text-slate-200"
                >
                  Activer l’actualité (visible immédiatement)
                </label>
              </div>
            </div>
          </div>

          {/* Section contenu */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Contenu du message
            </h4>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800">
              <ReactQuill
                value={formData.content}
                onChange={handleContentChange}
                className="quill-editor"
              />
            </div>
            <p className="text-[0.7rem] text-slate-500 dark:text-slate-400">
              Contenu identique ou très proche de l’e-mail envoyé aux clubs /
              enseignants. Vous pouvez mettre en forme (titres, listes, liens…).
            </p>
          </div>

          {/* Section image */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Illustration (optionnelle)
            </h4>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold">
                URL de l’image
              </label>
              <input
                type="url"
                name="image_url"
                placeholder="https://... (facultatif)"
                value={formData.image_url}
                onChange={handleChange}
                className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
              />
              <p className="text-[0.7rem] text-slate-500 dark:text-slate-400">
                L’image sera affichée en en-tête de la carte. Laissez vide si
                vous ne souhaitez pas d’illustration spécifique.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-3 pt-2 pb-4">
            <Button
              text="Enregistrer"
              icon={FaSave}
              type="submit"
              className="btn-primary gap-2"
            />
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={handleCancel}
              type="button"
              className="btn-ghost gap-2"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateNewsModal;
